import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, Search, Database, ArrowRight, ChevronDown } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { searchSapMaterials, getSapMaterial } from './materialApi';

const COLUMN_KEYS = ['matnr', 'mbrsh', 'mtart', 'maktx', 'meins', 'matkl', 'werks', 'vkorg', 'vtweg'];

/**
 * SapMaterialSearchDialog — SAP-Materialstamm durchsuchen, die (SAP-sichtbaren)
 * Felder je Treffer aufklappen und als Vorlage übernehmen (füllt die Maske vor).
 *
 * Props: user, config {fields}, onPick(templateData), onClose, showError
 */
const SapMaterialSearchDialog = ({ user, config, onPick, onClose, showError }) => {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'de';
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState({}); // sapId -> material
  const [busyId, setBusyId] = useState(null);
  const timer = useRef(null);

  const labelOf = useMemo(() => {
    const map = {};
    for (const f of (config?.fields || [])) map[f.key] = lang === 'en' ? f.labelEn : f.labelDe;
    // Org-/Kern-Labels, die evtl. nicht im Katalog stehen
    Object.assign(map, { matnr: t('material.matnr'), werks: t('material.werks') });
    return (k) => map[k] || k;
  }, [config, lang, t]);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const res = await searchSapMaterials(user, query);
      setRows(res.materials || []);
    } catch { showError?.(t('material.sapSearchError')); }
    finally { setLoading(false); }
  }, [user, showError, t]);

  useEffect(() => { load(''); }, [load]);

  const onInput = (v) => {
    setQ(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(v), 300);
  };

  const ensureDetail = useCallback(async (sapId) => {
    if (detail[sapId]) return detail[sapId];
    setBusyId(sapId);
    try {
      const { material } = await getSapMaterial(user, sapId);
      setDetail((d) => ({ ...d, [sapId]: material }));
      return material;
    } catch { showError?.(t('material.sapLoadError')); return null; }
    finally { setBusyId(null); }
  }, [detail, user, showError, t]);

  const toggle = async (sapId) => {
    if (openId === sapId) { setOpenId(null); return; }
    setOpenId(sapId);
    await ensureDetail(sapId);
  };

  const pick = async (r) => {
    const material = await ensureDetail(r.sapId);
    if (!material) return;
    const tpl = {
      id: null, status: 'draft',
      fields: material.fields || {},
      texts: material.texts || {},
      reference_matnr: material.reference || r.matnr,
    };
    COLUMN_KEYS.forEach((k) => { if (material.fields?.[k] != null) tpl[k] = material.fields[k]; });
    onPick?.(tpl);
  };

  const panel = isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-slate-200';
  const inputBase = isDarkMode ? 'bg-white/5 border-white/15 text-white placeholder-white/30' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400';
  const rowBg = isDarkMode ? 'bg-white/5' : 'bg-slate-50';

  const fieldGrid = (material) => {
    const entries = Object.entries(material?.fields || {});
    if (!entries.length) return <div className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('material.sapNoVisibleFields')}</div>;
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex flex-col min-w-0">
            <span className={`text-[10px] ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>{labelOf(k)}</span>
            <span className={`text-sm truncate ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>{String(v)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onMouseDown={onClose}>
      <div className={`w-full max-w-xl rounded-2xl shadow-2xl ${panel} flex flex-col max-h-[85vh]`} onMouseDown={(e) => e.stopPropagation()}>
        <div className={`flex items-start gap-3 p-5 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-blue-500/15' : 'bg-blue-50'}`}>
            <Database className="w-5 h-5 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('material.sapLoadTitle')}</h3>
            <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('material.sapLoadSubtitle')}</p>
          </div>
          <button type="button" onClick={onClose} className={`p-1 rounded-lg ${isDarkMode ? 'text-white/50 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-100'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />
            <input autoFocus value={q} onChange={(e) => onInput(e.target.value)} placeholder={t('material.sapSearchPlaceholder')}
              className={`w-full rounded-lg border pl-9 pr-3 py-2 text-sm outline-none ${inputBase}`} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
          {loading ? (
            <div className={`text-center text-sm py-8 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{t('common.loading')}</div>
          ) : rows.length === 0 ? (
            <div className={`text-center text-sm py-8 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('material.sapNoResults')}</div>
          ) : rows.map((r) => {
            const isOpen = openId === r.sapId;
            return (
              <div key={r.sapId} className={`rounded-xl ${rowBg}`}>
                <button type="button" onClick={() => toggle(r.sapId)} className="w-full text-left px-3 py-2 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono text-xs ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{r.matnr}</span>
                      {r.mtart && <span className={`text-[11px] ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>· {r.mtart}</span>}
                      {r.matkl && <span className={`text-[11px] ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>· {r.matkl}</span>}
                    </div>
                    <div className={`text-sm truncate ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>{r.maktx || '—'}</div>
                  </div>
                  <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                </button>
                {isOpen && (
                  <div className={`px-3 pb-3 pt-1 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200/70'}`}>
                    {busyId === r.sapId && !detail[r.sapId] ? (
                      <div className={`text-xs py-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('common.loading')}</div>
                    ) : (
                      <>
                        {fieldGrid(detail[r.sapId])}
                        <div className="flex justify-end mt-3">
                          <button type="button" onClick={() => pick(r)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700">
                            {t('material.sapUseAsTemplate')} <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SapMaterialSearchDialog;
